/**
 * Type definitions for Birthday Widget feature
 *
 * Defines types for birthday widget configuration, public display data,
 * and related data structures.
 */

/**
 * Display period options for birthday widget
 */
export type BirthdayDisplayPeriod = 'month' | 'week' | 'day';

/**
 * Name display format options
 */
export type BirthdayNameFormat = 'full' | 'first_only' | 'first_last_initial';

/**
 * Birthday Widget Configuration
 *
 * Controls how student birthdays are displayed on the public widget page.
 */
export interface BirthdayWidgetConfig {
  id: string;
  studio: string;
  studio_name: string;
  studio_slug?: string;
  display_period: BirthdayDisplayPeriod;
  name_display_format: BirthdayNameFormat;
  greeting_message: string;
  external_url: string;
  settings: Record<string, unknown>;
  embed_code: string;
  created_at: string;
  updated_at: string;
}

/**
 * Birthday Widget Configuration Update Payload
 */
export interface BirthdayWidgetConfigUpdate {
  display_period?: BirthdayDisplayPeriod;
  name_display_format?: BirthdayNameFormat;
  greeting_message?: string;
  external_url?: string;
  settings?: Record<string, unknown>;
}

/**
 * Student birthday data for public display
 */
export interface PublicBirthdayStudent {
  display_name: string;
  birthday: string; // ISO date string
  month: number;
  day: number;
}

/**
 * Studio information for public display
 */
export interface PublicStudioInfo {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  owner_plan_slug?: string | null;
}

/**
 * Configuration data for public display
 */
export interface PublicBirthdayConfig {
  display_period: BirthdayDisplayPeriod;
  name_display_format: BirthdayNameFormat;
  greeting_message: string;
  external_url: string;
}

/**
 * Theme data for public display
 */
export interface PublicThemeData {
  tokens: {
    colors: Record<string, unknown>;
    typography: Record<string, unknown>;
    shape: Record<string, unknown>;
    modes: Record<string, unknown>;
  };
  generated: Record<string, unknown>;
}

/**
 * Complete public birthday widget response
 */
export interface PublicBirthdayWidget {
  config: PublicBirthdayConfig;
  studio: PublicStudioInfo;
  students: PublicBirthdayStudent[];
  theme: PublicThemeData | null;
}

/**
 * Display period option for UI selection
 */
export interface DisplayPeriodOption {
  value: BirthdayDisplayPeriod;
  label: string;
  description: string;
}

/**
 * Name format option for UI selection
 */
export interface NameFormatOption {
  value: BirthdayNameFormat;
  label: string;
  example: string;
}

/**
 * Constants for display period options
 */
export const DISPLAY_PERIOD_OPTIONS: DisplayPeriodOption[] = [
  {
    value: 'month',
    label: 'Current Month',
    description: 'Show all birthdays in the current month',
  },
  {
    value: 'week',
    label: 'Within 7 Days',
    description: 'Show birthdays occurring in the next 7 days',
  },
  {
    value: 'day',
    label: 'Today Only',
    description: 'Show only birthdays happening today',
  },
];

/**
 * Constants for name format options
 */
export const NAME_FORMAT_OPTIONS: NameFormatOption[] = [
  {
    value: 'full',
    label: 'Full Name',
    example: 'John Smith',
  },
  {
    value: 'first_only',
    label: 'First Name Only',
    example: 'John',
  },
  {
    value: 'first_last_initial',
    label: 'First Name + Last Initial',
    example: 'John S.',
  },
];
